import { useState, useMemo } from "react";
import { Search, X } from "lucide-react";
import { AgentCard } from "@/components/agent/AgentCard";
import { AgentCategoryTabs } from "@/components/agent/AgentCategoryTabs";
import { useAgentStore } from "@/store/AgentStore";
import { mockAgents, type AgentCategoryId } from "@/constants/agents";
import type { Agent } from "@/types/agent";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";

interface AgentMarketplaceProps {
  onNavigate?: (path: string, agentId?: string) => void;
}

export function AgentMarketplace({ onNavigate }: AgentMarketplaceProps) {
  const { setCurrentAgent, setAgentDismissed } = useAgentStore();
  const [selectedCategory, setSelectedCategory] = useState<AgentCategoryId>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredAgents = useMemo(() => {
    return mockAgents.filter((agent) => {
      const matchesCategory = selectedCategory === "all" || agent.category === selectedCategory;
      const matchesSearch =
        searchQuery.trim() === "" ||
        agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  const handleEnter = (agent: Agent) => {
    setCurrentAgent(agent);
    setAgentDismissed(false);
    onNavigate?.("chat");
  };

  const handleSettings = (agent: Agent) => {
    onNavigate?.("settings", agent.id);
  };

  return (
    <div className="h-screen bg-background overflow-y-auto">
      <div className="container mx-auto px-4 py-8 max-w-6xl min-h-full">
        <div className="flex flex-col gap-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-bold text-foreground">智能体广场</h1>
              <Button variant="ghost" size="sm" onClick={() => onNavigate?.("chat")}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="搜索智能体..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <AgentCategoryTabs
            selectedCategory={selectedCategory}
            onSelect={setSelectedCategory}
          />
        </div>

        {filteredAgents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAgents.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                onEnter={handleEnter}
                onSettings={handleSettings}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-muted-foreground">未找到匹配的智能体</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AgentMarketplace;
